/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  useState, useEffect, ReactElement, FormEvent, ChangeEvent, useCallback,
} from 'react';
import {
  Container, Row, Col, Button, Form,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Keycloak, { KeycloakInstance } from 'keycloak-js';
import { StateRedux } from '../../store/type-redux';
import {
  api, setTokenAfterLogin, alerts,
} from '../../utils';
import { load } from '../../components';
import logoBlack from '../../images/logo-mtrix-black.png';
import bgMtrix from '../../images/page-title-mtrix.png';

const Texto = (textoToReplace: any) => {
  const { t } = useTranslation();
  return (
    t(`login.${textoToReplace}`)
  );
};
const HtmlToast = (texto: any) => {
  const { textoToReplace } = texto;
  return (
    <div>
      {Texto(textoToReplace)}
    </div>
  );
};

const Index = (): ReactElement => {
  const dispatch = useDispatch();

  const {
    language: { languageSelected: language },
  } = useSelector((state: StateRedux) => state);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [keycloak, setKeycloak] = useState<KeycloakInstance | undefined>();

  const setLoginAuthentication = useCallback(async (
    token: string,
    usuario: any,
    refreshToken: string,
  ) => {
    await setTokenAfterLogin(token);

    localStorage.setItem('refreshToken', refreshToken);

    const { data: { produtos: aplicacoes } } = await api.getAplicacoesDoUsuario();
    // Verifica se usuário já aceitou os termos retorna true ou false
    const acceptTerms = await api.getTermos();

    dispatch({
      type: 'ACCESS',
      access: {
        termos: acceptTerms.data.acceptTerms,
        resetPassword: true,
      },
    });

    dispatch({
      type: 'USER_REALM',
      realm: {
        nameUsuario: usuario.nomeDoUsuario,
        emailUsuario: usuario.emailDoUsuario,
        nameRealm: usuario.nomeDoRealm,
        servicoId: usuario.clientId,
      },
    });

    dispatch({
      type: 'APP',
      aplicacoes,
    });

    navigate('/home');
  }, [dispatch, navigate]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!username) {
      toast.warn(<HtmlToast textoToReplace="usuarioObrigatorio" />);
      return;
    }

    if (!password) {
      toast.warn(<HtmlToast textoToReplace="senhaObrigatoria" />);
      return;
    }

    try {
      load.show();

      const {
        data: { token, usuario, refresh_token: refreshToken },
      } = await api.login({ username, password });

      await setLoginAuthentication(token, usuario, refreshToken);
    } catch (error: any) {
      localStorage.setItem('token', '');
      localStorage.setItem('refreshToken', '');
      alerts.exibirErrorCatch(error);
    } finally {
      load.hide();
    }
  };

  const handlerSubmitAzureAd = () => {
    const { REACT_APP_BASE_API } = process.env;

    if (!keycloak) {
      alerts.exibirToastWarn('Erro com keycloak');
      return;
    }

    keycloak.login({
      redirectUri: REACT_APP_BASE_API,
      scope: 'openid profile email',
    })
      .error((error) => alerts.exibirErrorCatch(error));
  };

  const setLanguage = (languageSelected: 'en' | 'pt') => {
    dispatch({
      type: 'LANGUAGE',
      language: {
        languageSelected,
      },
    });
  };

  const initKeycloak = useCallback(() => {
    const {
      REACT_APP_URL_KEYCLOAK, REACT_APP_REALM, REACT_APP_CLIENT_ID,
    } = process.env;

    const keyk = Keycloak({
      url: REACT_APP_URL_KEYCLOAK,
      realm: REACT_APP_REALM,
      clientId: REACT_APP_CLIENT_ID,
    });

    keyk.onAuthSuccess = async () => {
      if (!keyk.token || !keyk.refreshToken) {
        return;
      }

      dispatch({
        type: 'KEYCLOAK',
        keycloak: keyk,
      });

      try {
        load.show();

        const decoded: JwtPayload & { preferred_username: string } = jwtDecode(keyk.token);

        const { data: usuario } = await api
          .getUserInfo({ username: decoded.preferred_username });

        await setLoginAuthentication(keyk.token, usuario, keyk.refreshToken);
      } catch (error: any) {
        localStorage.setItem('token', '');
        localStorage.setItem('refreshToken', '');
        navigate('/login');
      } finally {
        load.hide();
      }
    };

    keyk.onTokenExpired = async () => {
      try {
        load.show();
        keyk.updateToken(3601)
          .success(async () => {
            if (!keyk.token || !keyk.refreshToken) {
              return;
            }

            const decoded: JwtPayload & { preferred_username: string } = jwtDecode(keyk.token);

            const { data: usuario } = await api
              .getUserInfo({ username: decoded.preferred_username });

            await setLoginAuthentication(keyk.token, usuario, keyk.refreshToken);
          })
          .error(() => {
            keyk.clearToken();
            navigate('/login');
          });
      } catch (error: any) {
        localStorage.setItem('token', '');
        localStorage.setItem('refreshToken', '');
        navigate('/login');
      } finally {
        load.hide();
      }
    };

    keyk.init({ onLoad: 'check-sso' })
      .success(() => {
        setKeycloak(keyk);
      })
      .error((error) => alerts.exibirErrorCatch(error));
  }, [setLoginAuthentication, dispatch]);

  useEffect(() => {
    initKeycloak();
  }, [initKeycloak]);

  return (
    <>
      <Container>
        <div className="mt-2 mb-3">
          <a href="https://www.mtrix.com.br/">
            <img
              className="default_logo"
              src={logoBlack}
              style={{ height: 70 }}
              alt="Logo Mtrix"
            />
          </a>
        </div>
      </Container>
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          backgroundImage: `url(${bgMtrix})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'repeat',
          backgroundAttachment: 'scroll',
          backgroundPosition: 'center',
          backgroundColor: 'rgb(235 250 251 / 0%)',
          height: 210,
        }}
      >
        <h1
          style={{
            color: '#01bbee',
            fontSize: 42,
            fontFamily: 'Nunito Sans',
          }}
        >
          Login
        </h1>
      </div>
      <Container>
        <Row className="justify-content-center">
          <Col xl={6} lg={12} md={9}>
            <div className="card o-hidden border-0 shadow-lg mt-4">
              <div className="card-body p-0">
                <Row>
                  <Col lg={12}>
                    <div className="p-5">
                      <Form onSubmit={handleLogin}>
                        <Form.Group className="form-group">
                          <Form.Control
                            type="text"
                            className="form-control form-control-user"
                            placeholder={t('login.usuario')}
                            onInput={(e: ChangeEvent<HTMLInputElement>) => (
                              setUsername(e.target.value)
                            )}
                          />
                        </Form.Group>
                        <Form.Group className="form-group">
                          <Form.Control
                            type="password"
                            className="form-control form-control-user"
                            placeholder={t('login.senha')}
                            onInput={(e: ChangeEvent<HTMLInputElement>) => (
                              setPassword(e.target.value)
                            )}
                          />
                        </Form.Group>
                        <Button
                          type="submit"
                          className="btn btn-primary btn-user btn-block"
                          variant="primary"
                        >
                          {t('login.acessar')}
                        </Button>
                        <hr />
                        <Button
                          type="button"
                          className="btn btn-block btn-outline-secondary"
                          variant="light"
                          onClick={handlerSubmitAzureAd}
                        >
                          {t('login.acesso_brf')}
                        </Button>
                      </Form>
                      <div className="mt-4 text-center">
                        <Link
                          to="/forgot-password"
                          className="m--font-boldest"
                          style={{ textDecoration: 'none' }}
                        >
                          {t('login.esqueceu')}
                        </Link>
                      </div>
                      <div>
                        <Button
                          className={`btn ${language === 'en' ? 'btn-primary' : 'btn-light'} btn-circle btn-sm`}
                          onClick={() => setLanguage('en')}
                        >
                          EN
                        </Button>
                        {' '}
                        <Button
                          className={`btn ${language === 'pt' ? 'btn-primary' : 'btn-light'} btn-circle btn-sm`}
                          onClick={() => setLanguage('pt')}
                        >
                          PT
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Index;
